import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { Observable, Subject, combineLatest, filter, map, of, takeUntil, tap, catchError } from 'rxjs';
import { Store } from '@ngxs/store';
import { IORD_Order, SapNumber, SapNumType } from '../../../interfaces/request.interface';
import { SignalRService, SapNumberUpdatedEvent } from '../../../services/signalr.service';
import { RequestActions } from '../../../store/action/request.action';
import { RTAApiClient } from '../../../services/rta-api.service';
import { GlobalNotificationService } from '../../../services/globalnotification.service';

@Component({
    selector: 'app-sap-part-numbers',
    templateUrl: './sap-part-numbers.component.html',
    styleUrls: ['./sap-part-numbers.component.css'],
    standalone: false
})
export class SapPartNumbersComponent implements OnInit, OnDestroy {
    @Input() request$!: Observable<IORD_Order | undefined>;
    @Input() fixedCutterSapData$?: Observable<any>;
    @Input() orderId!: number;
    
    public showAddBitDialog = false;
    public isRefreshing = false;
    public sapNumbers$!: Observable<SapNumber[]>;
    public showCreateEquipmentTemplate$!: Observable<boolean>;
    public isCreatingEquipmentTemplate = false;
    public sapNumberTypes$!: Observable<SapNumType[]>;

    private destroy$ = new Subject<void>();
    private currentOrderId: number | null = null;
    private sapNumTypeMap = new Map<string, SapNumType>();

    constructor(
        private store: Store,
        private signalRService: SignalRService,
        private rtaApi: RTAApiClient,
        private notification: GlobalNotificationService
    ) {}

    ngOnInit(): void {
        this.sapNumbers$ = combineLatest([
            this.request$,
            this.fixedCutterSapData$ ?? of(undefined)
        ]).pipe(
            map(([request, fixedCutter]) => {
                const requestSapNumbers = request?.sapNumbers ?? [];
                if (requestSapNumbers.length > 0) {
                    return requestSapNumbers;
                }
                const fallbackSapNumbers = (fixedCutter as any)?.sapNumbers ?? [];
                return Array.isArray(fallbackSapNumbers) ? fallbackSapNumbers : [];
            })
        );
        this.sapNumberTypes$ = this.rtaApi.getSapNumberTypes().pipe(
            map(types => Array.isArray(types) ? types : []),
            tap(types => {
                const entries: Array<[string, SapNumType]> = [];
                for (const t of types) {
                    const rawId = (t as any)?.id ?? (t as any)?.Id;
                    if (rawId === null || rawId === undefined) {
                        continue;
                    }
                    const key = String(rawId);
                    entries.push([key, t]);
                }
                this.sapNumTypeMap = new Map(entries);
            }),
            catchError(() => {
                this.sapNumTypeMap.clear();
                return of([]);
            })
        );
        this.showCreateEquipmentTemplate$ = this.sapNumbers$.pipe(
            map(items => items.some(item => item?.sapNumTypeId === 3 || item?.sapNumType?.id === 3))
        );

        // Subscribe to request$ to get the orderId and join the SignalR group
        this.request$.pipe(
            filter((order): order is IORD_Order => !!order),
            takeUntil(this.destroy$)
        ).subscribe(order => {
            const newOrderId = order.id ?? this.orderId;
            
            // If we have a new order ID, join its SignalR group
            if (newOrderId && newOrderId !== this.currentOrderId) {
                // Leave the old group if we were in one
                if (this.currentOrderId) {
                    this.signalRService.leaveOrderGroup(this.currentOrderId).catch(() => {});
                }
                
                // Join the new order group
                this.currentOrderId = newOrderId;
                this.joinOrderGroup(newOrderId);
            }
        });

        // Listen for SAP number updates via SignalR
        this.signalRService.onSapNumberUpdated.pipe(
            filter((event): event is SapNumberUpdatedEvent => !!event),
            filter(event => event.orderId === this.currentOrderId),
            takeUntil(this.destroy$)
        ).subscribe(event => {
            console.log('📨 Received SAP number update for order:', event.orderId);
            // Refresh the request data from the store
            this.refreshData();
        });
    }

    ngOnDestroy(): void {
        // Leave the SignalR group when component is destroyed
        if (this.currentOrderId) {
            this.signalRService.leaveOrderGroup(this.currentOrderId).catch(() => {});
        }
        
        this.destroy$.next();
        this.destroy$.complete();
    }

    private joinOrderGroup(orderId: number): void {
        // Ensure SignalR is connected before joining the group
        if (this.signalRService.isConnected()) {
            this.signalRService.joinOrderGroup(orderId).catch(err => {
                console.warn('Could not join order group, SignalR may not be available:', err);
            });
        } else {
            // If not connected, try to connect first
            console.log('SignalR not connected, attempting to connect...');
            // The connection will be established by the app component or a parent component
        }
    }

    openAddBitDialog(): void {
        this.showAddBitDialog = true;
    }

    closeAddBitDialog(): void {
        this.showAddBitDialog = false;
    }

    onBitCreated(): void {
        this.showAddBitDialog = false;
        // Refresh data after creating a bit (in case SignalR doesn't push the update)
        this.refreshData();
    }

    getSapNumberTypeDescription(item: SapNumber): string {
        const rawId = item.sapNumTypeId ?? (item.sapNumType as any)?.id ?? (item.sapNumType as any)?.Id;
        if (rawId !== null && rawId !== undefined) {
            const type = this.sapNumTypeMap.get(String(rawId));
            const desc = (type as any)?.description ?? (type as any)?.Description;
            if (desc) {
                return desc;
            }
        }
        const fallbackDesc = (item.sapNumType as any)?.description ?? (item.sapNumType as any)?.Description;
        const fallbackName = (item.sapNumType as any)?.name ?? (item.sapNumType as any)?.Name;
        return fallbackDesc
            ?? fallbackName
            ?? (item.sapNumTypeId != null ? String(item.sapNumTypeId) : '');
    }

    createEquipmentTemplate(): void {
        const targetId = this.currentOrderId ?? this.orderId;
        if (!targetId) {
            this.notification.showWarningNotification('Order ID is not available.');
            return;
        }
        if (this.isCreatingEquipmentTemplate) {
            return;
        }
        this.isCreatingEquipmentTemplate = true;
        this.notification.showInfoNotification('Equipment template creation has been submitted. Please wait...');
        const payload = {
            target_Id: targetId,
            userId: this.rtaApi.$CURRENT_USER_ID
        } as any;
        this.rtaApi.createEquipmentTemplate(payload).subscribe({
            next: (response) => {
                this.isCreatingEquipmentTemplate = false;
                if (response?.status) {
                    this.notification.showSuccessNotification(response.message ?? 'Equipment template created.');
                    this.refreshData();
                } else {
                    this.notification.showWarningNotification(response?.message ?? 'Unable to create equipment template.');
                }
            },
            error: () => {
                this.isCreatingEquipmentTemplate = false;
                this.notification.showErrorNotification('Failed to create equipment template.');
            }
        });
    }

    /**
     * Manually refresh the SAP numbers data
     */
    refreshData(): void {
        if (!this.currentOrderId || this.isRefreshing) {
            return;
        }

        this.isRefreshing = true;
        console.log('🔄 Refreshing SAP numbers for order:', this.currentOrderId);
        
        // Dispatch action to reload the request data
        this.store.dispatch(new RequestActions.GetRequestDetails({ id: this.currentOrderId.toString() }))
            .subscribe({
                next: () => {
                    console.log('✅ SAP numbers refreshed successfully');
                    this.isRefreshing = false;
                },
                error: (err) => {
                    console.error('❌ Error refreshing SAP numbers:', err);
                    this.isRefreshing = false;
                }
            });
    }
}
